import { PeriodEnum } from '../model/PeriodEnum';
import { Organisation, OrganisationContact } from '../types/OrganisationType';
import { Dashboard, DashboardFilters, DashboardStatus, SavedFilter, SavedFilterDetails } from '../types/DashboardType';
import { DashboardStatusEnum } from '../model/DashboardStatusEnum';
import { FirmUser, Invite, InviteResult } from '../types/FirmType';
import { Stats } from '../types/StatsType';

function getCookie(name: string): string | undefined {
  return document.cookie
    .split('; ')
    .find(row => row.startsWith(name + '='))
    ?.split('=')[1];
}

function buildHeaders() {
  return {
    'Content-Type': 'application/json',
    'X-XSRF-TOKEN': getCookie('XSRF-TOKEN') || '',
  };
}

const fetchOptions = {
  mode: 'cors' as RequestMode,
  credentials: 'same-origin' as RequestCredentials,
};

// When the session has expired the backend redirects API calls to the login page. Fetch would
// quietly follow that redirect, so stop at it and send the browser to the login page instead.
async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const response = await fetch(path, { ...fetchOptions, headers: buildHeaders(), redirect: 'manual', ...init });
  if (response.type === 'opaqueredirect') {
    window.location.href = '/login';
    // Never settle, so the page doesn't flash an error while the browser leaves
    return new Promise(() => {});
  }
  return response;
}

export async function getCurrentUser(ignoreErrors = false) {
  const response = await fetch('/api/user/current', {
    ...fetchOptions,
    headers: buildHeaders(),
    redirect: 'manual',
  });

  if (response.status === 200) {
    return await response.json();
  }
  if (!ignoreErrors) {
    await handleApiError(response, 'Current user');
  }
}

export async function getSyncStatus(syncType: SyncType): Promise<SyncStatus | null> {
  let searchParams = { type: syncType };
  const response = await apiFetch('/api/user/sync-status?' + new URLSearchParams(searchParams));

  if (response.status === 200) {
    return await response.json();
  }

  return null;
}

export async function getDashboard(yearMonth: String | null) {
  let searchParams: any = {};
  if (yearMonth) {
    searchParams.yearMonth = yearMonth;
  }
  const orgResponse = await apiFetch('/api/dashboard?' + new URLSearchParams(searchParams));
  if (orgResponse.status === 200) {
    return await orgResponse.json();
  }

  await handleApiError(orgResponse, 'Dashboard');
}

export async function getOrganisations() {
  const orgResponse = await apiFetch('/api/organisation');
  if (orgResponse.status === 200) {
    return await orgResponse.json();
  }

  await handleApiError(orgResponse, 'Organisations');
}

type UpdateOrganisationRequest = {
  paygwPeriod?: PeriodEnum;
  autoReminderBasDue?: boolean;
  customFieldEmail?: string;
};

type BatchUpdateOrganisationRequest = {
  paygwPeriod?: PeriodEnum;
  autoReminderBasDue?: boolean;
  clientManagerId?: number;
  tag?: string;
  orgIds: number[];
};

export async function updateOrganisations(orgId: number, request: UpdateOrganisationRequest): Promise<Organisation> {
  return api('POST', `/api/organisation/${orgId}`, request);
}

export async function batchUpdateOrganisations(request: BatchUpdateOrganisationRequest): Promise<Organisation[]> {
  return api('POST', `/api/organisation/batch`, request);
}

export async function updateOrganisationContact(orgId: number, contactEmail: string): Promise<Organisation> {
  return api('POST', `/api/organisation/${orgId}/contact`, {
    email: contactEmail,
  });
}

export async function tagOrganisation(orgId: number, tag: string): Promise<Organisation> {
  return api('POST', `/api/organisation/${orgId}/tag/${encodeURIComponent(tag)}`);
}

export async function untagOrganisation(orgId: number, tag: string): Promise<Organisation> {
  return api('DELETE', `/api/organisation/${orgId}/tag/${encodeURIComponent(tag)}`);
}

export async function updateOrganisationManager(orgId: number, managerId: number): Promise<Organisation> {
  return api('POST', `/api/organisation/${orgId}/manager`, {
    id: managerId,
  });
}

export async function getEmailsLog() {
  return api('GET', '/api/emails/log');
}

/** The user's custom template, or undefined when they use the default. */
export async function getEmailTemplate(): Promise<EmailTemplate | undefined> {
  try {
    return await api('GET', '/api/emails/template');
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return undefined;
    throw e;
  }
}

export async function getDefaultEmailTemplate(): Promise<{ subject: string; body: string }> {
  return api('GET', '/api/emails/template/default');
}

type createEmailTemplateRequest = {
  id?: number;
  name: string;
  subject: string;
  body: string;
};

export async function createEmailTemplate(body: createEmailTemplateRequest) {
  return api('POST', '/api/emails/template', body);
}

export async function updateEmailTemplate(body: createEmailTemplateRequest) {
  return api('POST', `/api/emails/template/${body.id}`, body);
}

export async function deleteEmailTemplate(id: number) {
  return api('DELETE', `/api/emails/template/${id}`);
}

// Saved filters store statuses by key (PENDING) so renaming a label doesn't break them, but the table filters on the label
// it shows (Pending): convert them here, on the way in and out.
const statusLabels: Record<string, string> = { ...DashboardStatusEnum };
const withStatuses = (filters: DashboardFilters, convert: (status: string) => string): DashboardFilters =>
  filters.status ? { ...filters, status: filters.status.map(convert) } : filters;
const toStatusKey = (label: string) => Object.keys(statusLabels).find(key => statusLabels[key] === label) ?? label;
const toStatusLabel = (key: string) => statusLabels[key] ?? key;
const fromApi = (savedFilter: SavedFilter): SavedFilter => ({ ...savedFilter, filters: withStatuses(savedFilter.filters, toStatusLabel) });

export async function getSavedFilters(): Promise<SavedFilter[]> {
  return (await api('GET', '/api/dashboard/saved-filters')).map(fromApi);
}

export async function createSavedFilter(details: SavedFilterDetails, filters: DashboardFilters): Promise<SavedFilter> {
  return fromApi(await api('POST', '/api/dashboard/saved-filters', { ...details, filters: withStatuses(filters, toStatusKey) }));
}

// The default filters are changed by their status, the user's own by their id
function savedFilterUrl(savedFilter: SavedFilter) {
  const path = savedFilter.defaultStatus ? `defaults/${savedFilter.defaultStatus}` : savedFilter.id;
  return `/api/dashboard/saved-filters/${path}`;
}

export async function updateSavedFilter(savedFilter: SavedFilter, details: SavedFilterDetails): Promise<SavedFilter> {
  return fromApi(await api('PUT', savedFilterUrl(savedFilter), details));
}

export async function deleteSavedFilter(savedFilter: SavedFilter) {
  return api('DELETE', savedFilterUrl(savedFilter));
}

/** Forgets the user's changes to the default filters, and returns all their filters. */
export async function resetDefaultSavedFilters(): Promise<SavedFilter[]> {
  return (await api('POST', '/api/dashboard/saved-filters/reset-defaults')).map(fromApi);
}

export async function removeXeroConnection(orgId: number): Promise<Boolean> {
  const response = await apiFetch(`/api/organisation/${orgId}`, { method: 'DELETE' });
  return response.ok;
}

// The backend accepts the enum key (READY_TO_START) or a label that matches it ignoring case (Complete).
export async function updateDashboardEntries(dashboardIds: number[], status: DashboardStatusEnum | DashboardStatus): Promise<Dashboard[]> {
  return api('POST', `/api/dashboard`, {
    dashboardIds: dashboardIds,
    status: status,
  });
}

export async function setDashboardNote(dashboardId: number, note: string): Promise<Dashboard> {
  return api('POST', `/api/dashboard/${dashboardId}/note`, {
    note,
  });
}

export async function dismissSyncStatus(syncStatusId: number): Promise<void> {
  return api('POST', `/api/user/sync-status/${syncStatusId}/dismiss`);
}

export async function syncDashboardEntries(dashboardIds: number[]): Promise<Dashboard[]> {
  return api('POST', `/api/dashboard/sync`, dashboardIds);
}

export type EmailsPreviewRecipient = {
  dashboardId: number;
  organisationName: string;
  managerEmail?: string;
  firstName?: string;
  lastName?: string;
  email?: String;
};

export type EmailsPreviewResponse = {
  from: string;
  replyTo: string;
  to: EmailsPreviewRecipient[];
  subject: string;
  body: string;
};

export async function sendDashboardEmails(
  dashboardIds: number[],
  subjectTemplate: string,
  contentTemplate: string,
  attachTransactions: boolean,
): Promise<Map<string, string>> {
  const jsonResponse = await api('POST', `/api/dashboard/emails`, {
    dashboardIds,
    subjectTemplate,
    contentTemplate,
    attachTransactions,
  });

  return new Map<string, string>(Object.entries(jsonResponse.results));
}

export async function previewReminderEmails(dashboardIds: number[]): Promise<EmailsPreviewResponse> {
  return api('POST', `/api/dashboard/emails-preview`, { dashboardIds });
}

export async function syncTenants(orgIds: number[]): Promise<Organisation[]> {
  return api('POST', `/api/organisation/sync`, orgIds);
}

export async function getContacts(orgId: number): Promise<OrganisationContact[]> {
  return api('GET', `/api/organisation/${orgId}/contacts`);
}

export async function getOrgUsers(orgId: number): Promise<PublicUser[]> {
  return api('GET', `/api/organisation/${orgId}/users`);
}

export async function getFirmUsers(firmId: number): Promise<FirmUser[]> {
  return api('GET', `/api/firm/${firmId}/users`);
}

export async function getFirmPendingInvites(firmId: number): Promise<Invite[]> {
  return api('GET', `/api/firm/${firmId}/users/pending`);
}

export type PendingInvite = {
  id: number;
  firmName: string;
  invitedByName: string;
  firmOpenAccess: boolean;
};

export async function getPlans(): Promise<Plan[]> {
  return api('GET', '/api/subscription/plans');
}

export async function getPendingInvite(): Promise<PendingInvite | null> {
  // 204 when there's no invite
  return (await api('GET', '/api/user/invite')) ?? null;
}

export async function acceptInvite(inviteId: number): Promise<User> {
  return api('POST', `/api/user/invite/${inviteId}/accept`);
}

export async function declineInvite(inviteId: number): Promise<void> {
  return api('DELETE', `/api/user/invite/${inviteId}`);
}

export async function inviteUserToFirm(firmId: number, email: string): Promise<{ result: InviteResult }> {
  return api('POST', `/api/firm/${firmId}/users`, { email });
}

export async function updateFirm(firmId: number, name: string): Promise<Firm> {
  return api('POST', `/api/firm/${firmId}`, { name });
}

export async function updateFirmAccess(firmId: number, enabled: boolean): Promise<Firm> {
  return api('POST', `/api/firm/${firmId}/open-access`, { enabled });
}

export async function removeUserFromFirm(firmId: number, userId: number): Promise<void> {
  return api('DELETE', `/api/firm/${firmId}/user/${userId}`);
}

export async function sendSupportMessage(message: String, captchaValue: String, emailFrom?: String): Promise<void> {
  return api('POST', '/api/support/contact', { message, captchaValue, emailFrom });
}

export type PortalSessionResponse = {
  url?: string;
  hasStripeSubscription: boolean;
};

export async function createPortalSession(): Promise<PortalSessionResponse> {
  return api('POST', `/api/subscription/portal-session`);
}

export async function getPaymentMethod(): Promise<{ hasPaymentMethod: boolean }> {
  return api('GET', '/api/subscription/payment-method');
}

export async function runSyncDashboardsJob(yearMonth: string) {
  const syncResponse = await apiFetch(`/api/manage/syncAllDashboards?yearMonth=${yearMonth}`, {
    method: 'POST',
  });
  return syncResponse.status;
}

export async function getAllUsers(): Promise<User[]> {
  return api('GET', `/api/manage/users`);
}

export async function getAllInactiveUsers(): Promise<User[]> {
  return api('GET', `/api/manage/inactive-users`);
}

export async function getStats(): Promise<Stats> {
  return api('GET', `/api/manage/stats`);
}

export async function runSyncOrgJob() {
  const syncResponse = await apiFetch(`/api/manage/syncAllOrgs`, {
    method: 'POST',
  });
  return syncResponse.status;
}

export type XpmConnectedResponse = {
  connected: boolean;
  authenticationSuccessful: boolean;
  allScopes: boolean;
};

export async function isXpmConnected(checkAuth: boolean): Promise<XpmConnectedResponse> {
  return api('GET', `/api/xpm/connected?checkAuth=` + checkAuth);
}

async function handleApiError(response: Response, apiName: string) {
  const statusCode = response.status;

  if (statusCode === 302 || statusCode === 401 || statusCode === 403 || (statusCode === 0 && response.type === 'opaqueredirect')) {
    window.location.href = '/login';
    return;
  }

  if (statusCode === 402) {
    window.location.href = '/subscription';
    return;
  }

  console.error(`error ${statusCode} ${JSON.stringify(response)} on ${apiName} `, await response.text());

  if (statusCode === 500 || statusCode === 400) {
    window.location.href = `/error?status=${statusCode}&message=Issue while loading ${apiName}`;
    return;
  }

  // I think since we handle opaque redirect above we can all show some error in this use case
  // to be tested more.
  window.location.href = '/login';
  return;
}

/** A failed api call, with the HTTP status so callers can handle specific ones (e.g. 404). */
export class ApiError extends Error {
  constructor(
    readonly method: string,
    readonly path: string,
    readonly status: number,
  ) {
    super(`${method} ${path} api returned status ${status}`);
  }
}

/** Calls the backend and returns the parsed JSON, or undefined when the response has no body. */
async function api(method: 'GET' | 'POST' | 'PUT' | 'DELETE', path: string, body?: unknown): Promise<any> {
  const response = await apiFetch(path, { method, body: body === undefined ? undefined : JSON.stringify(body) });
  if (!response.ok) {
    throw new ApiError(method, path, response.status);
  }
  const text = await response.text();
  return text ? JSON.parse(text) : undefined;
}
